// Character model and derived stats. Every attribute/perk/cyberware effect lives here.
import {
  ATTRS, ATTR_IDS, AttrId, CONFIG, CYBER, CyberId, PERKS, PerkId, perkFx, PROGRESSION, RARITIES, Rarity,
  VEHICLE_UPGRADES, VehicleType, WEAPONS, WeaponId, AmmoType, VEHICLES,
} from './data';
import { VMods } from './sim';
import type { WeaponSpec } from './data';
import { Build, GUNS, ModularId, gunName, gunSpec, isModular, stockBuild } from './gunsmith';

/** b: the build of a modular gun (slot path -> part id); replaced, never edited in place */
export interface Item { uid: string; w: WeaponId | ModularId; r: Rarity; roll: number; b?: Build }
/** spare parts kept at most this many of each */
export const PARTS_BIN_MAX = 9;
export interface Appearance { skin: number; jacket: number; hair: number; portrait: number }
export interface Character {
  id: string; name: string; appearance: Appearance;
  money: number; level: number; xp: number; cred: number;
  /** lifetime money earned (never goes down), for the top-runners board */
  earned: number;
  attrs: Record<AttrId, number>; attrPoints: number; perkPoints: number; perks: Partial<Record<PerkId, number>>;
  /** how many PERKS.credPoints thresholds already paid out a perk point; respecs: how many respecs were bought (makes each respec claim key unique) */
  credPerks: number; respecs: number;
  /** recent attribute/perk spends, oldest first (`at` = server ms); the newest can be refunded free within PERKS.undoSec */
  undo?: SpendUndo[];
  /** story mode (?story=1) progress: finished beat ids; absent = nothing done */
  story?: { done: string[]; run?: number };
  inventory: Item[]; equipped: (string | null)[]; ammo: Record<AmmoType, number>;
  cyber: { owned: CyberId[]; installed: CyberId[]; active: CyberId | null };
  vehicles: { owned: VehicleType[]; upgrades: Partial<Record<VehicleType, string[]>> };
  /** spare gun parts: part id -> count (each capped at PARTS_BIN_MAX) */
  parts: Record<string, number>;
  /** ninja stars carried (0..CONFIG.stars.max); low-stakes like ammo */
  stars: number;
  /** smoke pellets carried (0..CONFIG.smoke.max) */
  smoke: number;
  /** EMP kunai carried (0..CONFIG.emp.max); low-stakes like ammo */
  emp: number;
}

/** Throwables on the G / T selector, in cycle order. `field` is the Character count; the first entry is the default. */
export const THROWABLES = [
  { id: 'star', field: 'stars', name: 'Ninja star', short: 'STARS' },
  { id: 'smoke', field: 'smoke', name: 'Smoke pellet', short: 'SMOKE' },
  { id: 'emp', field: 'emp', name: 'EMP kunai', short: 'EMP' },
] as const;
export type ThrowKind = typeof THROWABLES[number]['id'];
export const isThrowKind = (k: unknown): k is ThrowKind => THROWABLES.some(t => t.id === k);
/** The throwable after `cur` that the character owns (or `cur` itself when it owns no other). */
export function nextThrowable(c: Character, cur: ThrowKind): ThrowKind {
  const i = Math.max(0, THROWABLES.findIndex(t => t.id === cur));
  for (let k = 1; k <= THROWABLES.length; k++) { const t = THROWABLES[(i + k) % THROWABLES.length]; if ((c[t.field] ?? 0) > 0) return t.id; }
  return cur;
}

export const PALETTE = {
  skin: ['#f1c7a5', '#d49a74', '#a86c4a', '#6b4430', '#e8d0ff'],
  jacket: ['#ff3ea5', '#2de2c8', '#ffe03a', '#7a4dff', '#ff7a2d', '#e8e8f0', '#2a2a3a'],
  hair: ['#101018', '#f0f0ff', '#ff3ea5', '#2de2c8', '#ffe03a', '#9a5cff'],
  portraits: 4,
};

export function newCharacter(id: string, name: string): Character {
  const attrs = {} as Record<AttrId, number>;
  for (const a of ATTR_IDS) attrs[a] = ATTRS.start;
  return {
    id, name, appearance: { skin: 0, jacket: 0, hair: 0, portrait: 0 },
    money: PROGRESSION.startMoney, earned: 0, level: 1, xp: 0, cred: 0,
    attrs, attrPoints: ATTRS.startFreePoints, perkPoints: PERKS.startPoints, perks: {}, credPerks: 0, respecs: 0,
    inventory: [newItem('starter-pistol', 'pistol', 'common', 1)],
    equipped: ['starter-pistol', null, null], ammo: { ...PROGRESSION.startAmmo },
    cyber: { owned: [], installed: [], active: null },
    vehicles: { owned: ['beater'], upgrades: {} }, parts: {}, stars: 0, smoke: 0, emp: 0,
  };
}

export const perk = (c: Character, p: PerkId) => c.perks[p] ?? 0;
export const hasCyber = (c: Character, id: CyberId) => c.cyber.installed.includes(id);
export const cyberUsed = (c: Character) => c.cyber.installed.reduce((s, id) => s + CYBER.items[id].cost, 0);

/** The tuning numbers of a perk the character has, else null. */
const fxOf = (c: Character, id: PerkId) => (perk(c, id) > 0 ? perkFx(id) : null);

export function derived(c: Character) {
  const a = c.attrs;
  const arms = hasCyber(c, 'reinforced_arms') ? CYBER.items.reinforced_arms : null;
  const ram = fxOf(c, 'ram_plating'), medic = fxOf(c, 'field_medic'), hot = fxOf(c, 'hot_wire');
  return {
    // perk branches (tuning in perks.json fx); null = perk not owned
    tackle: fxOf(c, 'tackle'), lastStand: fxOf(c, 'last_stand'), deadEye: fxOf(c, 'dead_eye'),
    daisyChain: fxOf(c, 'daisy_chain'), greenWave: fxOf(c, 'green_wave'), getaway: fxOf(c, 'getaway_artist'),
    lookout: fxOf(c, 'lookout'), rally: fxOf(c, 'rally'),
    gunWeightMult: fxOf(c, 'light_kit')?.weightMult ?? 1,
    theftSightMult: hot?.witnessRange ?? 1, theftHeatMult: hot?.heatMult ?? 1,
    ramDealt: ram?.dealt ?? 1, ramTaken: ram?.taken ?? 1, ramBossDealt: ram?.bossDealt ?? 1,
    reviveSpeed: (medic?.reviveSpeed ?? 1) * (hasCyber(c, 'kizuna_link') ? CYBER.items.kizuna_link.reviveSpeed! : 1), reviveHpFrac: medic?.hpFrac ?? CONFIG.player.reviveHpFrac,
    fixerBonus: fxOf(c, 'fixers_cut')?.payBonus ?? 0,
    maxHp: CONFIG.player.baseHp + CONFIG.player.hpPerBody * a.body + (arms?.hpBonus ?? 0),
    armor: 15 * perk(c, 'thick_skin'),
    maxRam: CONFIG.player.baseRam + CONFIG.player.ramPerInt * a.intelligence + 2 * perk(c, 'overclock'),
    ramRegen: CONFIG.player.ramRegen * (1 + 0.05 * a.intelligence),
    moveMult: 1 + 0.025 * (a.reflexes - ATTRS.start),
    spreadMult: Math.max(0.3, (1 - 0.05 * (a.reflexes - ATTRS.start)) * (1 - 0.2 * perk(c, 'run_and_gun'))),
    reloadMult: 1 / ((1 + 0.04 * (a.reflexes - ATTRS.start)) * (1 + 0.1 * perk(c, 'run_and_gun'))),
    meleeMult: (1 + 0.07 * (a.body - ATTRS.start)) * (1 + 0.25 * perk(c, 'iron_knuckles')) * (arms?.meleeMult ?? 1),
    meleeKnockback: perk(c, 'iron_knuckles') >= 2,
    hackCdMult: 1 - 0.15 * perk(c, 'ghost_signal'),
    hackDurMult: (1 + 0.08 * (a.technical - ATTRS.start)) * (1 + 0.15 * perk(c, 'ghost_signal')),
    shortCircuitMult: 1 + 0.1 * (a.intelligence - ATTRS.start),
    detectMult: Math.max(0.5, 1 - 0.04 * (a.cool - ATTRS.start)) * (hasCyber(c, 'koban_scrambler') ? CYBER.items.koban_scrambler.detectMult! : 1),
    searchReduce: 0.5 * (a.cool - ATTRS.start),
    /** Koban Scrambler: seconds taken off a new search for each star */
    searchCutPerStar: hasCyber(c, 'koban_scrambler') ? CYBER.items.koban_scrambler.searchCutPerStar! : 0,
    vehicleDamageMult: Math.max(0.5, 1 - 0.04 * (a.technical - ATTRS.start)),
    pistolBounces: (WEAPONS.pistol.bounces ?? 1) + perk(c, 'ricochet_artist'),
    bounceKeep: perk(c, 'ricochet_artist') > 0 ? 1 : 0.75,
    driveBySpread: perk(c, 'run_and_gun') >= 2 ? 1 : 1.8,
  };
}
export type Derived = ReturnType<typeof derived>;

/** A new inventory item; modular guns come with their stock build. */
export function newItem(uid: string, w: WeaponId | ModularId, r: Rarity, roll: number): Item {
  return isModular(w) ? { uid, w, r, roll, b: stockBuild(GUNS[w]) } : { uid, w, r, roll };
}
const specCache = new WeakMap<Build, WeaponSpec>();
/** The weapon spec of an item: weapons.json for the original guns, computed from the build for modular ones. */
export function weaponSpec(item: Item | null): WeaponSpec {
  if (!item || !isModular(item.w)) return WEAPONS[(item?.w ?? 'fists') as WeaponId];
  const b = item.b ?? GUNS[item.w].defaults;
  let s = specCache.get(b); if (!s) { s = gunSpec(GUNS[item.w], b); specCache.set(b, s); }
  return s;
}
export function weaponName(w: string) { return isModular(w) ? gunName(GUNS[w]) : WEAPONS[w as WeaponId]?.name ?? w; }

export function weaponStats(item: Item | null, d: Derived) {
  const id = item?.w ?? 'fists';
  const w = weaponSpec(item);
  const r = RARITIES.tiers[item?.r ?? 'common'];
  const roll = item?.roll ?? 1;
  return {
    id, spec: w,
    damage: w.damage * r.damage * roll * (w.kind === 'melee' ? d.meleeMult : 1),
    chargedDamage: (w.chargedDamage ?? 0) * r.damage * roll,
    mag: Math.round(w.mag * r.mag),
    reload: w.reload * r.reload * d.reloadMult,
    spread: w.spread * d.spreadMult,
    climb: (w.climb ?? 0) * d.spreadMult, wobble: (w.wobble ?? 0) * d.spreadMult,
  };
}

export function xpForNext(level: number) { return PROGRESSION.xpPerLevel[Math.min(level, PROGRESSION.levelCap - 1)]; }
/** Adds xp and applies level-ups. Returns number of levels gained. */
export function addXp(c: Character, xp: number): number {
  if (c.level >= PROGRESSION.levelCap) return 0;
  c.xp += xp; let gained = 0;
  while (c.level < PROGRESSION.levelCap && c.xp >= xpForNext(c.level)) {
    c.xp -= xpForNext(c.level); c.level++; gained++;
    c.attrPoints += ATTRS.pointsPerLevel; c.perkPoints += PERKS.pointsPerLevel;
  }
  if (c.level >= PROGRESSION.levelCap) c.xp = 0;
  return gained;
}

/** Perk points that cred milestones (PERKS.credPoints) now owe the character; mutates and returns how many were added. */
export function grantCredPerks(c: Character): number {
  const reached = PERKS.credPoints.filter(t => c.cred >= t).length;
  const add = Math.max(0, reached - (c.credPerks ?? 0));
  if (add) { c.perkPoints += add; c.credPerks = reached; }
  return add;
}
export const perksSpent = (c: Character) => Object.values(c.perks).reduce((s, r) => s + (r ?? 0), 0);
export const attrsSpent = (c: Character) => ATTR_IDS.reduce((s, a) => s + Math.max(0, c.attrs[a] - ATTRS.start), 0);
/** Doc Neko's price to refund every spent perk and attribute point. */
export const respecCost = (c: Character) => Math.min(PERKS.respec.max, PERKS.respec.base + PERKS.respec.perPoint * (perksSpent(c) + attrsSpent(c)));

export interface SpendUndo { k: 'attr' | 'perk'; id: string; at: number }
/** Records a spend for the free undo; drops entries that can no longer be undone. */
export function noteSpend(c: Character, k: SpendUndo['k'], id: string, now: number) {
  c.undo = [...(c.undo ?? []).filter(u => now - u.at < PERKS.undoSec * 1000), { k, id, at: now }].slice(-20);
}
/** The spend a free undo would take back now (always the newest, so the tree stays valid), else null. */
export function undoable(c: Character, now: number): SpendUndo | null {
  const u = c.undo?.[c.undo.length - 1];
  if (!u || now - u.at >= PERKS.undoSec * 1000) return null;
  if (u.k === 'perk' ? !(c.perks[u.id as PerkId]! > 0) : !(c.attrs[u.id as AttrId] > ATTRS.start)) return null;
  return u;
}
/** Takes back the newest spend if undoable; returns it, else null. */
export function undoSpend(c: Character, now: number): SpendUndo | null {
  const u = undoable(c, now); if (!u) return null;
  c.undo!.pop();
  if (u.k === 'perk') { const id = u.id as PerkId; c.perks[id]! -= 1; if (!c.perks[id]) delete c.perks[id]; c.perkPoints++; }
  else { c.attrs[u.id as AttrId]--; c.attrPoints++; }
  return u;
}
const perkEntries = () => Object.entries(PERKS.perks) as [PerkId, (typeof PERKS.perks)[PerkId]][];

/**
 * Brings a saved character up to the current perk tree (idempotent; returns true if anything changed):
 * perks that no longer exist, ranks above the cap and the second pick of a choice group are removed and
 * refunded as perk points; cred milestones are paid retroactively. Valid old perks keep their ranks.
 */
export function normalizeCharacter(c: Character): boolean {
  let changed = false;
  const stars = Number.isFinite(c.stars) ? Math.max(0, Math.min(CONFIG.stars.max, Math.floor(c.stars))) : 0;
  if (stars !== c.stars) { c.stars = stars; changed = true; }
  const smoke = Number.isFinite(c.smoke) ? Math.max(0, Math.min(CONFIG.smoke.max, Math.floor(c.smoke))) : 0;
  if (smoke !== c.smoke) { c.smoke = smoke; changed = true; }
  const emp = Number.isFinite(c.emp) ? Math.max(0, Math.min(CONFIG.emp.max, Math.floor(c.emp))) : 0;
  if (emp !== c.emp) { c.emp = emp; changed = true; }
  const before = c.perkPoints;
  c.perks ??= {};
  if (typeof c.respecs !== 'number') { c.respecs = 0; changed = true; }
  if (typeof c.credPerks !== 'number') { c.credPerks = 0; changed = true; }
  let refund = 0;
  for (const [id, raw] of Object.entries(c.perks) as [PerkId, number][]) {
    const spec = PERKS.perks[id]; const rank = Number.isFinite(raw) ? Math.floor(raw) : 0;
    const keep = spec ? Math.max(0, Math.min(spec.maxRank, rank)) : 0;
    refund += Math.max(0, rank) - keep;
    if (keep !== raw) { changed = true; if (keep > 0) c.perks[id] = keep; else delete c.perks[id]; }
  }
  const taken = new Set<string>();
  for (const [id, spec] of perkEntries()) {
    if (!spec.choiceGroup || !perk(c, id)) continue;
    if (taken.has(spec.choiceGroup)) { refund += perk(c, id); delete c.perks[id]; changed = true; } else taken.add(spec.choiceGroup);
  }
  c.perkPoints += refund;
  if (grantCredPerks(c)) changed = true;
  return changed || c.perkPoints !== before;
}

export function canRankPerk(c: Character, id: PerkId): string | null {
  const p = PERKS.perks[id]; if (!p) return 'unknown perk';
  const r = perk(c, id);
  if (r >= p.maxRank) return 'max rank';
  if (c.perkPoints < 1) return 'no perk points';
  if (r === 0 && p.tier === 2) {
    const opener = perkEntries().find(([, q]) => q.branch === p.branch && q.tier === 1)!;
    if (!perk(c, opener[0])) return `needs ${opener[1].name}`;
    const rival = perkEntries().find(([k, q]) => k !== id && q.choiceGroup === p.choiceGroup)!;
    if (perk(c, rival[0])) return `you chose ${rival[1].name}`;
  }
  if (r === 0 && p.tier === 3 && !perkEntries().some(([k, q]) => q.branch === p.branch && q.tier === 2 && perk(c, k))) return 'needs a tier 2 perk';
  const req = r === 0 ? p.req : p.req2;
  for (const [a, v] of Object.entries(req)) if (c.attrs[a as AttrId] < (v as number)) return `needs ${ATTRS.attributes[a as AttrId].name} ${v}`;
  return null;
}

export function vehicleMods(c: Character | null, t: VehicleType): VMods & { hp: number } {
  const m = { accel: 1, grip: 1, turn: 1, speed: 1, hp: 1, drift: 0 };
  if (!c) return m;
  m.drift = fxOf(c, 'drift_king')?.boost ?? 0;
  for (const u of c.vehicles.upgrades[t] ?? []) {
    const up = VEHICLE_UPGRADES[u]; if (!up) continue;
    m.accel *= up.accel; m.grip *= up.grip; m.turn *= up.turn; m.speed *= up.speed; m.hp *= up.hp;
  }
  return m;
}

/** Ryujin Overclock: the same mods with the active implant's speed and acceleration boost on top (server and client prediction both use this). */
export function overclockMods<T extends VMods>(m: T): T {
  const o = CYBER.items.ryujin_overclock;
  return { ...m, speed: m.speed * o.speedMult!, accel: m.accel * o.accelMult! };
}

/** Clinic fee; going down while wanted costs a share of your cash instead, if that is more. */
export function respawnCost(c: Character, wanted = false) {
  const flat = Math.min(CONFIG.respawn.cap, CONFIG.respawn.base + CONFIG.respawn.perLevel * (c.level - 1));
  return wanted ? Math.max(flat, Math.floor(c.money * CONFIG.respawn.wantedCashFrac)) : flat;
}
export function starsFor(points: number) { let s = 0; for (const t of CONFIG.police.starThresholds) if (points >= t) s++; return s; }
export function weaponPrice(w: WeaponId | ModularId, r: Rarity) { return Math.round((isModular(w) ? gunSpec(GUNS[w], GUNS[w].defaults).price : WEAPONS[w].price) * RARITIES.tiers[r].priceMult); }
/** What Hachi's would charge for this exact item: the gun with its fitted parts, times its rarity. */
export function itemValue(it: Item) {
  const base = isModular(it.w) ? gunSpec(GUNS[it.w], it.b ?? GUNS[it.w].defaults).price : WEAPONS[it.w].price;
  return Math.round(base * RARITIES.tiers[it.r].priceMult);
}
/** What Hachi's pays for a gun or a spare part worth `value`. */
export const sellPrice = (value: number) => Math.round(value * CONFIG.shop.sellFraction);
export const vehicleName = (t: VehicleType) => VEHICLES[t].name;
/** Aim error (rad, each side) for a shot: drivers get `driveBySpread`; any seated shooter is 1.2x worse, except in an open-bed seat. */
export function shotSpread(base: number, driveBySpread: number, veh: { spec: { openBed?: number[] } } | null, seat: number): number {
  if (!veh) return base;
  return base * (seat === 0 ? driveBySpread : 1) * (veh.spec.openBed?.includes(seat) ? 1 : 1.2);
}
